"use strict";
/**
 * Backend proxy URL resolution for PR preview environments.
 *
 * This module provides a clean API for resolving service URLs that route through
 * the backend-proxy in PR preview environments. It uses the APP_ENV typed accessor
 * from @buildwithflux/shared (which wraps the build-time environment variable from
 * scripts/version) to detect PR builds. In PR preview environments, APP_ENV has
 * the format "preview-{PR_NUMBER}".
 *
 * @example
 * import {AppService, AutoPlacement} from "@buildwithflux/models";
 *
 * // Using the namespace (default: only uses proxy in PR preview)
 * fetch(AppService.AutoPlacement.resolve("/start_job"), { ... });
 *
 * // Or import the resolver directly
 * fetch(AutoPlacement.resolve("/start_job"), { ... });
 *
 * // For internal tools that need proxy routing in all environments
 * fetch(AutoPlacement.resolve("/start_job", {forceProxy: true}), { ... });
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.AppService = exports.Autopilot = exports.AutoLayout = exports.AutoPlacement = exports.ProxiedService = void 0;
exports.getBackendProxyBaseUrl = getBackendProxyBaseUrl;
exports.getBackendProxyBaseUrlForAllEnvs = getBackendProxyBaseUrlForAllEnvs;
const shared_1 = require("@buildwithflux/shared");
/**
 * Enum for proxied backend services that can be accessed through the backend-proxy.
 * These services are routed through the Cloudflare backend-proxy worker.
 */
var ProxiedService;
(function (ProxiedService) {
    ProxiedService["AutoLayout"] = "auto-layout";
    ProxiedService["AutoPlacement"] = "auto-placement";
    ProxiedService["Autopilot"] = "autopilot";
})(ProxiedService || (exports.ProxiedService = ProxiedService = {}));
// Production CloudRun hosts (fallback for non-PR environments)
// These match the defaults in auto-placement/api.ts and auto_layout/api.ts
const AUTO_PLACEMENT_DEFAULT_HOST = "https://auto-placement-app-myfpimhjlq-wl.a.run.app";
const AUTO_LAYOUT_DEFAULT_HOST = "https://auto-layout-app-myfpimhjlq-wl.a.run.app";
const AUTOPILOT_DEFAULT_HOST = "https://autopilot-app-myfpimhjlq-wl.a.run.app";
/**
 * Gets the backend-proxy base URL for PR preview environments.
 *
 * This function uses the build-time APP_ENV environment variable (from scripts/version)
 * to determine if we're in a PR preview environment. In PR preview builds, APP_ENV
 * has the format "preview-{PR_NUMBER}". This is the canonical source for version info.
 *
 * @returns The backend-proxy base URL for PR preview environments, or undefined otherwise
 *
 * @example
 * // In a PR preview build (APP_ENV="preview-1234")
 * getBackendProxyBaseUrl() // returns "https://pr-1234.app.flux.ai"
 *
 * // In production or local builds (APP_ENV="production" or "local")
 * getBackendProxyBaseUrl() // returns undefined
 */
function getBackendProxyBaseUrl() {
    const prNumber = (0, shared_1.getPrNumber)();
    if (prNumber) {
        return `https://pr-${prNumber}.app.flux.ai`;
    }
    return undefined;
}
/**
 * Constructs the full URL for a proxied service endpoint in PR preview environments.
 *
 * This function only returns a URL for PR preview environments. For all other environments,
 * it returns undefined to allow callers to fall back to their default API URLs.
 *
 * @param service - The proxied service to call
 * @param path - The API path (e.g., "/start_job", "/pause_job")
 * @returns The full URL for the service endpoint in PR preview environments, or undefined otherwise
 *
 * @example
 * // In a PR preview build (REACT_APP_GITHUB_PR_ID=1234)
 * getProxiedServiceUrl(ProxiedService.AutoPlacement, "/start_job")
 * // returns "https://pr-1234.app.flux.ai/auto-placement/start_job"
 *
 * // In production or local builds
 * getProxiedServiceUrl(ProxiedService.AutoPlacement, "/start_job")
 * // returns undefined
 */
function getProxiedServiceUrl(service, path) {
    const baseUrl = getBackendProxyBaseUrl();
    if (!baseUrl) {
        return undefined;
    }
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    return `${baseUrl}/${service}${normalizedPath}`;
}
/**
 * Gets the fallback host for a service when not in a PR preview environment.
 * Checks environment variables first, then falls back to default production hosts.
 *
 * Note: AutoLayout is not included here because it uses alwaysUseProxy: true,
 * which means it always routes through the proxy and never needs a fallback host.
 */
function getFallbackHost(service) {
    if (service === ProxiedService.AutoPlacement) {
        return process.env.REACT_APP_AUTOPLACEMENT_HOST ?? AUTO_PLACEMENT_DEFAULT_HOST;
    }
    if (service === ProxiedService.Autopilot) {
        return process.env.REACT_APP_AUTOPILOT_HOST ?? AUTOPILOT_DEFAULT_HOST;
    }
    // This case should not be reached for AutoLayout since it uses alwaysUseProxy
    return AUTO_LAYOUT_DEFAULT_HOST;
}
/**
 * Gets the dev host environment variable for a service, if set.
 * This is used for local development to override the default proxy routing.
 * When set, requests go directly to the dev host instead of through the proxy.
 */
function getDevHost(service) {
    if (service === ProxiedService.AutoPlacement) {
        return process.env.REACT_APP_AUTOPLACEMENT_HOST;
    }
    if (service === ProxiedService.Autopilot) {
        return process.env.REACT_APP_AUTOPILOT_HOST;
    }
    if (service === ProxiedService.AutoLayout) {
        return process.env.REACT_APP_AUTOLAYOUT_DEV_HOST;
    }
    return undefined;
}
/**
 * Gets the path prefix for a service in production/localhost environments.
 */
function getProductionPathPrefix(service) {
    if (service === ProxiedService.Autopilot) {
        return `/${service}`;
    }
    return "";
}
// Production backend-proxy URL (used when forceProxy is true in non-PR environments)
const PRODUCTION_PROXY_BASE_URL = "https://app.flux.ai";
/**
 * Gets the backend-proxy base URL for all environments.
 * This is useful for internal tools that need to access the proxy in any environment.
 *
 * @returns The backend-proxy base URL:
 *   - In PR preview: "https://pr-{N}.app.flux.ai"
 *   - In production/localhost: "https://app.flux.ai"
 *
 * @example
 * import {getBackendProxyBaseUrlForAllEnvs} from "@buildwithflux/models";
 * const baseUrl = getBackendProxyBaseUrlForAllEnvs();
 * fetch(`${baseUrl}/config`, { ... });
 */
function getBackendProxyBaseUrlForAllEnvs() {
    return getBackendProxyBaseUrl() ?? PRODUCTION_PROXY_BASE_URL;
}
/**
 * Creates a service resolver that provides a clean API for resolving service URLs.
 * In PR preview environments, routes through the backend-proxy.
 * In other environments, falls back to the configured or default host (unless alwaysUseProxy is true).
 */
function createServiceResolver(service, serviceOptions) {
    return {
        /**
         * Resolves the full URL for an API endpoint.
         *
         * @param path - The API path (e.g., "/start_job", "/pause_job")
         * @param options - Optional configuration for URL resolution
         * @returns The full URL for the service endpoint
         *
         * @example
         * // In a PR preview build (APP_ENV="preview-1234")
         * AutoPlacement.resolve("/start_job")
         * // returns "https://pr-1234.app.flux.ai/auto-placement/start_job"
         *
         * // In production or local builds (default behavior)
         * AutoPlacement.resolve("/start_job")
         * // returns "https://auto-placement-app-myfpimhjlq-wl.a.run.app/start_job"
         *
         * // With forceProxy option (for internal tools)
         * AutoPlacement.resolve("/start_job", {forceProxy: true})
         * // returns "https://app.flux.ai/auto-placement/start_job" (in production)
         * // returns "https://pr-1234.app.flux.ai/auto-placement/start_job" (in PR preview)
         */
        resolve(path, options) {
            const normalizedPath = path.startsWith("/") ? path : `/${path}`;
            // When stripPrefix is false, the path already includes the service prefix
            // and the proxy will preserve it when forwarding to the backend.
            const preservePrefix = serviceOptions?.stripPrefix === false;
            if (options?.forceProxy) {
                const baseUrl = getBackendProxyBaseUrl() ?? PRODUCTION_PROXY_BASE_URL;
                return preservePrefix ? `${baseUrl}${normalizedPath}` : `${baseUrl}/${service}${normalizedPath}`;
            }
            if (serviceOptions?.alwaysUseProxy) {
                const devHost = getDevHost(service);
                if (devHost) {
                    return `${devHost}${normalizedPath}`;
                }
                const baseUrl = getBackendProxyBaseUrl() ?? PRODUCTION_PROXY_BASE_URL;
                return `${baseUrl}${normalizedPath}`;
            }
            // For services that don't always use proxy, check if we're in PR preview
            if (preservePrefix) {
                const baseUrl = getBackendProxyBaseUrl();
                if (baseUrl) {
                    return `${baseUrl}${normalizedPath}`;
                }
                return `${getFallbackHost(service)}${normalizedPath}`;
            }
            const proxyUrl = getProxiedServiceUrl(service, normalizedPath);
            if (proxyUrl) {
                return proxyUrl;
            }
            return `${getFallbackHost(service)}${getProductionPathPrefix(service)}${normalizedPath}`;
        },
    };
}
/**
 * Auto-Placement service resolver.
 * Use this to resolve URLs for the auto-placement backend service.
 *
 * @example
 * import {AutoPlacement} from "@buildwithflux/models";
 * fetch(AutoPlacement.resolve("/start_job"), { ... });
 */
exports.AutoPlacement = createServiceResolver(ProxiedService.AutoPlacement);
/**
 * Auto-Layout service resolver.
 * Uses stripPrefix: false for perfect path symmetry - the proxy preserves the /auto-layout prefix.
 */
exports.AutoLayout = createServiceResolver(ProxiedService.AutoLayout, {
    alwaysUseProxy: true,
    stripPrefix: false,
});
/**
 * Autopilot service resolver.
 * Use this to resolve URLs for the autopilot backend service.
 *
 * Note: Autopilot routes use the service name as a path prefix (e.g., /autopilot/start).
 * Pass paths without the prefix (e.g., "/start") and the resolver will add it automatically
 * in both PR preview and production environments.
 *
 * @example
 * import {Autopilot} from "@buildwithflux/models";
 * // In PR preview: https://pr-1234.app.flux.ai/autopilot/start
 * // In production: https://autopilot-app-myfpimhjlq-wl.a.run.app/autopilot/start
 * fetch(Autopilot.resolve("/start"), { ... });
 */
exports.Autopilot = createServiceResolver(ProxiedService.Autopilot);
/**
 * Namespace containing all proxied backend service resolvers.
 * Provides a convenient way to access service resolvers.
 *
 * @example
 * import {AppService} from "@buildwithflux/models";
 * fetch(AppService.AutoPlacement.resolve("/start_job"), { ... });
 */
exports.AppService = {
    AutoPlacement: exports.AutoPlacement,
    AutoLayout: exports.AutoLayout,
    Autopilot: exports.Autopilot,
};
//# sourceMappingURL=backendProxy.js.map